import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Sidebar from './Sidebar';
import styles from '../styles/BlogPage.module.css';
import axiosInstance from '../services/axiosInstance';
import Loader from '../components/Loader';
import RichTextEditor from '../components/RichTextEditor';
import {
  Send,
  RefreshCw,
  MessageSquare,
  Heart,
  Share2,
  Edit,
  Trash2,
  Image,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Link,
  Key,
  CircleUser,
  Home,
  LineChart,
  MessagesSquare,
  MoreVertical,
  Pencil
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { stripHtmlTags } from '../utils/HtmlStrip';
import DialogBox from '../components/dialogbox/DialogBox';
import { formatIndianDate } from '../utils/timeUtils';

const ThreadContext = React.createContext();

const ThreadContextProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return { name: 'Loading...' };
  });
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: () => {},
  });

  const handleLike = async (postId) => {
    try {
      if (!user) {
        setDialogConfig({
          isOpen: true,
          title: 'Login Required',
          message: 'Please login to like posts',
          type: 'info',
          onConfirm: () => setDialogConfig(prev => ({ ...prev, isOpen: false }))
        });
        return null;
      }

      const response = await axiosInstance.post(`/blogs/like/${postId}`);
      if (response.data.success) {
        setLikedPosts(prevLiked => {
          const newLiked = new Set(prevLiked);
          if (response.data.liked) {
            newLiked.add(postId);
          } else {
            newLiked.delete(postId);
          }
          return newLiked;
        });

        return {
          liked: response.data.liked,
          likesCount: response.data.likes_count || 0 // Ensure we get a number
        };
      }
      return null;
    } catch (error) {
      console.error('Error liking post:', error);
      return null;
    }
  };

  const handleComment = async (postId, comment) => {
    if (!user || !comment.trim()) return null;
    try {
      const response = await axiosInstance.post(`/blogs/comment/${postId}`, {
        content: comment
      });
      if (response.data.success) {
        return response.data.comment;
      }
      return null;
    } catch (error) {
      console.error('Error adding comment:', error);
      return null;
    }
  };

  const handleShare = async (post) => {
    try {
      const shareUrl = post.type === "blog"
        ? `${window.location.origin}/${post._id}/${post.title?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`
        : `${window.location.origin}/threads-blogs?thread=${post._id}`;

      const shareData = {
        title: post.title || 'Shared Post',
        text: post.content,
        url: shareUrl
      };

      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const value = {
    user,
    setUser,
    likedPosts,
    setLikedPosts,
    handleLike,
    handleComment,
    handleShare,
    dialogConfig,
    setDialogConfig
  };

  return (
    <ThreadContext.Provider value={value}>
      {children}
    </ThreadContext.Provider>
  );
};

const SendIcon = () => <Send size={18} />;
const RefreshIcon = () => <RefreshCw size={18} />;
const MessageIcon = () => <MessageSquare size={18} />;
const HeartIcon = () => <Heart size={18} />;
const ShareIcon = () => <Share2 size={18} />;
const EditIcon = () => <Edit size={18} />;
const DeleteIcon = () => <Trash2 size={18} />;
const ImageIcon = () => <Image size={18} />;

const validateImage = (file) => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  if (!file) return { valid: false, error: 'No file selected' };

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload JPG, PNG, GIF, or WebP images only.'
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'File is too large. Maximum size is 5MB.'
    };
  }

  return { valid: true };
};

const EditThreadModal = ({ isOpen, onClose, thread, onSave }) => {
  const [editedContent, setEditedContent] = useState(thread?.content || '');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(thread?.image || null);

  useEffect(() => {
    if (thread) {
      setEditedContent(thread.content || '');

      if (thread.image) {
        const imageUrl = thread.image.startsWith('http')
          ? thread.image
          : `${axiosInstance.defaults.baseURL}${thread.image}`;

        setImagePreview(imageUrl);
      } else {
        setImagePreview(null);
      }
    }
  }, [thread]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    onSave(thread._id, editedContent, image);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Edit Post</h2>
          <button className={styles.closeButton} onClick={onClose}>&times;</button>
        </div>

        <textarea
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          className={styles.textarea}
          placeholder="Update your post..."
          style={{ color: '#ffffff' }}
        />

        <div className={styles.imageInput} style={{ marginTop: '1rem' }}>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            id="threadImage"
          />
          <label htmlFor="threadImage" className={styles.imageUploadButton}>
            <ImageIcon /> {imagePreview ? 'Change Image' : 'Add Image'}
          </label>
        </div>

        {imagePreview && (
          <div className={styles.threadImagePreview}>
            <img
              src={imagePreview}
              alt="Preview"
              onError={(e) => {
                console.error("Failed to load image preview:", imagePreview);
                if (thread && thread.image) {
                  const imageName = thread.image.split(/[\/\\]/).pop();
                  e.target.src = `${axiosInstance.defaults.baseURL}/static/uploads/${imageName}`;
                }
              }}
            />
            <button
              className={styles.removeImageButton}
              onClick={() => {
                setImage(null);
                setImagePreview(null);
              }}
            >
              &times;
            </button>
          </div>
        )}

        <div className={styles.modalButtons}>
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
          <button
            className={styles.saveButton}
            onClick={handleSubmit}
            disabled={!editedContent.trim()}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const ExpandedThreadModal = ({ thread: initialThread, onClose }) => {
  const [thread, setThread] = useState(initialThread);
  const [commentText, setCommentText] = useState('');
  const [localLikesCount, setLocalLikesCount] = useState(initialThread.likes_count || 0);
  const [isPostingComment, setIsPostingComment] = useState(false);
  const { user, likedPosts, handleLike, handleComment, handleShare } = useContext(ThreadContext);

  const handleLikeClick = async (e) => {
    e.stopPropagation();
    const result = await handleLike(thread._id);
    if (result) {
      setLocalLikesCount(result.likesCount);
      setThread(prev => ({
        ...prev,
        likes_count: result.likesCount
      }));
    }
  };

  const handleCommentSubmit = async () => {
    if (!user || !commentText.trim()) return;
    
    try {
      setIsPostingComment(true);
      const result = await handleComment(thread._id, commentText);
      if (result) {
        setThread(prev => ({
          ...prev,
          comments: [...(prev.comments || []), result]
        }));
        setCommentText('');
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setIsPostingComment(false);
    }
  };

  if (!thread) return null;

  return (
    <div className={styles.expandedModal} onClick={onClose}>
      <div className={styles.expandedContent} onClick={e => e.stopPropagation()}>
        <button className={styles.modalCloseButton} onClick={onClose}>&times;</button>

        <div className={styles.postHeader}>
          <div className={styles.authorContainer}>
            <div className={styles.authorAvatar}>
              {thread.author.name.charAt(0)}
            </div>
            <div className={styles.authorInfo}>
              <span className={styles.postAuthor}>{thread.author.name}</span>
              <span className={styles.postDate}>
                {formatIndianDate(thread.created_at)}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.expandedThreadContent}>
          {thread.image && (
            <div className={styles.expandedImageContainer}>
              <img
                src={thread.image}
                alt="Thread"
                onError={(e) => {
                  console.error("Failed to load image:", thread.image);
                  e.target.src = '/placeholder-image.jpg';
                }}
              />
            </div>
          )}

          <p className={styles.expandedPostContent}>{thread.content}</p>
        </div>

        <div className={styles.expandedPostActions}>
          <button
            className={`${styles.actionButton} ${likedPosts.has(thread._id) ? styles.liked : ''}`}
            onClick={handleLikeClick}
          >
            <HeartIcon /> {localLikesCount}
          </button>
          <button className={styles.actionButton}>
            <MessageIcon /> {thread.comments?.length || 0}
          </button>
          <button
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              handleShare(thread);
            }}
          >
            <ShareIcon />
          </button>
        </div>

        <div className={styles.commentSection}>
          <div className={styles.commentInput}>
            <textarea
              className={styles.commentTextarea}
              placeholder={user ? "Write a comment..." : "Please login to comment"}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={!user || isPostingComment}
            />
            <button
              className={`${styles.commentButton} ${isPostingComment ? styles.loading : ''}`}
              onClick={handleCommentSubmit}
              disabled={!user || !commentText.trim() || isPostingComment}
            >
              {isPostingComment ? <RefreshIcon className={styles.spin} /> : 'Post'}
            </button>
          </div>

          <div className={styles.comments}>
            {thread.comments?.length > 0 ? (
              thread.comments.map((comment) => (
                <div key={comment._id} className={styles.comment}>
                  <div className={styles.commentHeader}>
                    <div className={styles.commentAuthor}>
                      <div className={styles.authorAvatar}>
                        {comment.author.name.charAt(0)}
                      </div>
                      <span>{comment.author.name}</span>
                    </div>
                    <span className={styles.commentDate}>
                      {formatIndianDate(comment.created_at)}
                    </span>
                  </div>
                  <p className={styles.commentContent}>{comment.content}</p>
                </div>
              ))
            ) : (
              <p className={styles.emptyComments}>No comments yet. Be the first to comment!</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateThreadModal = ({ isOpen, onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = validateImage(file);
      if (!validation.valid) {
        alert(validation.error);
        return;
      }
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() && !image) return;
    
    setIsSubmitting(true);
    await onSubmit(content, image);
    setContent('');
    setImage(null);
    setImagePreview(null);
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Create New Post</h2>
          <button className={styles.closeButton} onClick={onClose}>&times;</button>
        </div>

        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className={styles.textarea}
          placeholder="What's on your mind?"
        />

        <div className={styles.imageInput}>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            id="createThreadImage"
          />
          <label htmlFor="createThreadImage" className={styles.imageUploadButton}>
            <ImageIcon /> {imagePreview ? 'Change Image' : 'Add Image'}
          </label>
        </div>

        {imagePreview && (
          <div className={styles.threadImagePreview}>
            <img src={imagePreview} alt="Preview" />
            <button
              className={styles.removeImageButton}
              onClick={() => {
                setImage(null);
                setImagePreview(null);
              }}
            >
              &times;
            </button>
          </div>
        )}

        <div className={styles.modalButtons}>
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
          <button
            className={`${styles.saveButton} ${isSubmitting ? styles.loading : ''}`}
            onClick={handleSubmit}
            disabled={(!content.trim() && !image) || isSubmitting}
          >
            {isSubmitting ? <RefreshIcon className={styles.spin} /> : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ThreadsAndBlogs = () => {
  const { user, setUser, likedPosts, setLikedPosts } = useContext(ThreadContext);
  const [threads, setThreads] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [newThread, setNewThread] = useState('');
  const [newBlog, setNewBlog] = useState({ title: '', content: '', image: null });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('threads');
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [threadImage, setThreadImage] = useState(null);
  const [threadImagePreview, setThreadImagePreview] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEditThread, setCurrentEditThread] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  const [expandedThread, setExpandedThread] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [likesCountMap, setLikesCountMap] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const { handleLike, handleShare, dialogConfig, setDialogConfig } = useContext(ThreadContext);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let userData = null;

      if (token) {
        const userResponse = await axiosInstance.get('/auth/verify-token');
        if (userResponse.data.success && userResponse.data.user) {
          userData = userResponse.data.user;
          userData._id = userResponse.data.user._id || userResponse.data.userId;
          setUser(userData);
        }
      }

      const threadsResponse = await axiosInstance.get('/blogs/');
      if (threadsResponse.data.success) {
        const fetchedThreads = threadsResponse.data.data;
        setThreads(fetchedThreads);

        // Initialize liked posts from fetched data
        if (userData) {
          const userLikedPosts = new Set(
            fetchedThreads
              .filter(thread => thread.likes?.includes(userData._id))
              .map(thread => thread._id)
          );
          setLikedPosts(userLikedPosts);
          
          // Initialize likes count map
          const likesMap = fetchedThreads.reduce((acc, thread) => {
            acc[thread._id] = thread.likes_count || 0;
            return acc;
          }, {});
          setLikesCountMap(likesMap);
        }
      }

      const blogsResponse = await axiosInstance.get('/blogs/list');
      setBlogs(blogsResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest(`.${styles.menuButton}`)) {
        setShowMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    const initialLikesMap = threads.reduce((acc, thread) => {
      // Use the server-provided likes count, fallback to 0 if undefined
      acc[thread._id] = thread.likes_count || 0;
      return acc;
    }, {});
    setLikesCountMap(initialLikesMap);
  }, [threads]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const handleThreadImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = validateImage(file);
      if (!validation.valid) {
        setDialogConfig({
          isOpen: true,
          title: 'Invalid Image',
          message: validation.error,
          type: 'warning',
          onConfirm: () => setDialogConfig({ ...dialogConfig, isOpen: false })
        });
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setThreadImagePreview(reader.result);
        setThreadImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleThreadSubmit = async (content, image) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      if (content.trim()) {
        formData.append('content', content);
      }

      if (image) {
        const validation = validateImage(image);
        if (!validation.valid) {
          throw new Error(validation.error);
        }
        formData.append('image', image);
      }

      const response = await axiosInstance.post('/blogs/create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const newThreadData = response.data.data;
        setThreads([newThreadData, ...threads]);
      }
    } catch (error) {
      console.error('Error posting thread:', error);
      setDialogConfig({
        isOpen: true,
        title: 'Error',
        message: error.message || 'Failed to create post',
        type: 'error',
        onConfirm: () => setDialogConfig(prev => ({ ...prev, isOpen: false }))
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThreadLike = async (threadId, e) => {
    e.stopPropagation();
    const result = await handleLike(threadId);
    if (result) {
        // Update the likes count map with the server response
        setLikesCountMap(prev => ({
            ...prev,
            [threadId]: result.likesCount
        }));
        
        // Update the threads array to maintain consistency
        setThreads(prevThreads =>
            prevThreads.map(t =>
                t._id === threadId
                    ? { ...t, likes_count: result.likesCount }
                    : t
            )
        );
    }
  };

  const handleBlogSubmit = async () => {
    if (!user || !newBlog.title.trim() || !newBlog.content.trim()) return;

    try {
      const formData = new FormData();
      formData.append('title', newBlog.title);
      formData.append('content', newBlog.content);
      if (newBlog.image) {
        const validation = validateImage(newBlog.image);
        if (!validation.valid) {
          throw new Error(validation.error);
        }
        formData.append('image', newBlog.image);
      }

      const response = await axiosInstance.post('/blogs/create-blog', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload Progress: ${progress}%`);
        }
      });

      if (response.data.success) {
        setBlogs([response.data.data, ...blogs]);
        setNewBlog({ title: '', content: '', image: null });
        setShowBlogForm(false);

        setDialogConfig({
          isOpen: true,
          title: 'Success',
          message: 'Blog post created successfully!',
          type: 'success',
          onConfirm: () => setDialogConfig(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (error) {
      console.error('Error posting blog:', error);
      setDialogConfig({
        isOpen: true,
        title: 'Error',
        message: error.message || 'Failed to create blog post. Please try again.',
        type: 'error',
        onConfirm: () => setDialogConfig(prev => ({ ...prev, isOpen: false }))
      });
    }
  };

  const handleBlogCardClick = (blog) => {
    const slug = blog.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    navigate(`/${blog._id}/${slug}`);
  };

  const handleEditThread = async (threadId, content, image) => {
    try {
      const formData = new FormData();
      formData.append('content', content);
      if (image instanceof File) {
        formData.append('image', image);
      }

      const response = await axiosInstance.put(`/blogs/edit/${threadId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setThreads(prevThreads =>
          prevThreads.map(thread =>
            thread._id === threadId
              ? response.data.data
              : thread
          )
        );
        setEditModalOpen(false);
        setCurrentEditThread(null);
      }
    } catch (error) {
      console.error('Error updating thread:', error);
      alert('Failed to update post. Please try again.');
    }
  };

  const handleDeleteThread = async (threadId) => {
    setDialogConfig({
      isOpen: true,
      title: 'Delete Post',
      message: 'Are you sure you want to delete this post? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await axiosInstance.delete(`/blogs/delete/${threadId}`);
          if (response.data.success) {
            setThreads(prevThreads => prevThreads.filter(thread => thread._id !== threadId));
            setDialogConfig({
              isOpen: true,
              title: 'Success',
              message: 'Post deleted successfully.',
              type: 'success',
              onConfirm: () => setDialogConfig({ ...dialogConfig, isOpen: false }),
            });
          }
        } catch (error) {
          console.error('Error deleting thread:', error);
          setDialogConfig({
            isOpen: true,
            title: 'Error',
            message: 'Failed to delete post. Please try again.',
            type: 'error',
            onConfirm: () => setDialogConfig({ ...dialogConfig, isOpen: false }),
          });
        }
      },
    });
  };

  const renderBlogCard = (blog) => (
    <div
      key={blog._id}
      className={styles.blogCard}
      onClick={() => handleBlogCardClick(blog)}
    >
      <div className={styles.blogImage}>
        {blog.image ? (
          <img
            src={blog.image}
            alt={blog.title}
            onError={(e) => {
              console.error("Failed to load blog image:", blog.image);
              e.target.src = '/placeholder-image.jpg';
            }}
          />
        ) : (
          <div className={styles.placeholderImage}>
            <span>{blog.title.charAt(0)}</span>
          </div>
        )}
      </div>
      <div className={styles.blogContent}>
        <h3 className={styles.blogTitle}>{blog.title}</h3>
        <p className={styles.blogExcerpt}>
          {stripHtmlTags(blog.content).substring(0, 150)}...
        </p>
        <div className={styles.blogFooter}>
          <div className={styles.blogAuthor}>
            <div className={styles.authorAvatar}>
              {blog.author.name.charAt(0)}
            </div>
            <span>{blog.author.name}</span>
          </div>
          <span className={styles.blogDate}>
            {formatIndianDate(blog.created_at)}
          </span>
        </div>
      </div>
    </div>
  );

  const renderThread = (thread) => {
    return (
      <article 
        key={thread._id} 
        className={styles.thread}
        itemScope 
        itemType="https://schema.org/SocialMediaPosting"
        onClick={(e) => {
          // Only open if not clicking action buttons
          if (!e.target.closest(`.${styles.actionButton}`) && 
              !e.target.closest(`.${styles.menuButton}`)) {
            setExpandedThread(thread);
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <meta itemProp="datePublished" content={thread.created_at} />
        <meta itemProp="author" content={thread.author.name} />
        <meta itemProp="interactionCount" content={`UserLikes:${thread.likes_count || 0}`} />
        <meta itemProp="interactionCount" content={`UserComments:${thread.comments?.length || 0}`} />
        <meta itemProp="headline" content={thread.content.slice(0, 110)} />
        
        <div className={styles.postHeader}>
          <div className={styles.authorContainer} itemProp="author" itemScope itemType="https://schema.org/Person">
            <div className={styles.authorAvatar}>
              {thread.author.name.charAt(0)}
            </div>
            <div className={styles.authorInfo}>
              <span className={styles.postAuthor} itemProp="name">{thread.author.name}</span>
              <time className={styles.postDate} itemProp="datePublished" dateTime={thread.created_at}>
                {formatIndianDate(thread.created_at)}
              </time>
            </div>
          </div>

          {user && user._id === thread.user_id && (
            <button
              className={styles.menuButton}
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(showMenu === thread._id ? null : thread._id);
              }}
            >
              <MoreVertical size={18} />
            </button>
          )}

          {showMenu === thread._id && (
            <div className={styles.dropdown}>
              <button
                className={styles.dropdownItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentEditThread(thread);
                  setEditModalOpen(true);
                  setShowMenu(null);
                }}
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                className={`${styles.dropdownItem} ${styles.delete}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteThread(thread._id);
                  setShowMenu(null);
                }}
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          )}
        </div>

        <div className={styles.threadContent}>
          {thread.image && (
            <div className={styles.threadImagePreview}>
              <img
                src={thread.image}
                alt={`Post by ${thread.author.name}`}
                itemProp="image"
              />
            </div>
          )}

          <p className={styles.postContent} itemProp="text">{thread.content}</p>

          <div className={styles.postActions} onClick={(e) => e.stopPropagation()}>
            <button
              className={`${styles.actionButton} ${likedPosts.has(thread._id) ? styles.liked : ''}`}
              onClick={(e) => handleThreadLike(thread._id, e)}
            >
              <HeartIcon /> {likesCountMap[thread._id] ?? thread.likes_count ?? 0}
            </button>
            <button
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                setExpandedThread(thread);
              }}
            >
              <MessageIcon /> {thread.comments?.length || 0}
            </button>
            <button
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleShare(thread);
              }}
            >
              <ShareIcon />
            </button>
          </div>
        </div>
      </article>
    );
  };

  const getNavItems = (user) => {
    if (!user || user.name === 'Guest') {
      return [
        { name: 'Home', icon: <Home size={20} />, path: '/', ariaLabel: 'Home' },
        { name: 'Thoughts', icon: <MessagesSquare size={20} />, path: '/threads-blogs', ariaLabel: 'Community Threads & Blogs' },
        { name: 'Login', icon: <CircleUser size={20} />, path: '/login', ariaLabel: 'Login' },
        { name: 'Signup', icon: <Key size={20} />, path: '/signup', ariaLabel: 'Signup' },
      ];
    }
    return [
      { name: 'Dashboard', icon: <Home size={20} />, path: '/dashboard', ariaLabel: 'Dashboard' },
      { name: 'Analytics', icon: <LineChart size={20} />, path: '/analytics', ariaLabel: 'Analytics' },
      { name: 'Thoughts', icon: <MessagesSquare size={20} />, path: '/threads-blogs', ariaLabel: 'Community Threads & Blogs' },
      { name: 'Feedback', icon: <MessageSquare size={20} />, path: '/feedback', ariaLabel: 'Feedback' }
    ];
  };

  const handleBlogContentChange = (content) => {
    setNewBlog({ ...newBlog, content });
  };

  const generateThreadStructuredData = (thread) => ({
    "@context": "https://schema.org",
    "@type": "SocialMediaPosting",
    "headline": thread.content.slice(0, 110),
    "datePublished": thread.created_at,
    "dateModified": thread.updated_at || thread.created_at,
    "author": {
      "@type": "Person",
      "name": thread.author.name
    },
    "text": thread.content,
    "interactionStatistic": [
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/LikeAction",
        "userInteractionCount": thread.likes_count || 0
      },
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/CommentAction",
        "userInteractionCount": thread.comments?.length || 0
      }
    ]
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Community Threads & Discussions | StockKhata</title>
        <meta name="description" content="Join our community discussions, share your thoughts, and connect with other users." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
        
        {/* OpenGraph tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Community Threads & Discussions | StockKhata" />
        <meta property="og:description" content="Join our community discussions, share your thoughts, and connect with other users." />
        <meta property="og:url" content={window.location.href} />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Community Threads & Discussions | StockKhata" />
        <meta name="twitter:description" content="Join our community discussions, share your thoughts, and connect with other users." />
      </Helmet>

      {/* Add structured data for all threads */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": threads.map((thread, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": generateThreadStructuredData(thread)
          }))
        })}
      </script>

      <Sidebar
        user={user || { name: 'Guest' }}
        onLogout={handleLogout}
        navItems={getNavItems(user)}
      />
      <main className={styles.main}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'threads' ? styles.active : ''}`}
            onClick={() => setActiveTab('threads')}
          >
            Thoughts
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'blogs' ? styles.active : ''}`}
            onClick={() => setActiveTab('blogs')}
          >
            Blogs
          </button>
        </div>

        {activeTab === 'threads' && (
          <div className={styles.threads}>
            <div className={styles.feed}>
              {threads.length === 0 ? (
                <div className={styles.emptyMessage}>
                  <p>No threads yet. Be the first to start a conversation!</p>
                </div>
              ) : (
                threads.map(renderThread)
              )}
            </div>
            
            {user && (
              <button
                className={styles.createButton}
                onClick={() => setShowCreateModal(true)}
                aria-label="Create new post"
              >
                <Pencil size={24} />
              </button>
            )}
          </div>
        )}

        {activeTab === 'blogs' && (
          <div className={styles.blogs}>
            {user && (
              <button
                className={styles.addBlogButton}
                onClick={() => setShowBlogForm(!showBlogForm)}
              >
                {showBlogForm ? 'Cancel' : '+ Create New Blog'}
              </button>
            )}

            {showBlogForm && user && (
              <div className={styles.blogForm}>
                <input
                  type="text"
                  placeholder="Enter your blog title"
                  value={newBlog.title}
                  onChange={(e) => setNewBlog({ ...newBlog, title: e.target.value })}
                  className={styles.titleInput}
                  style={{ color: '#111827' }}
                />
                <ReactQuill
                  value={newBlog.content}
                  onChange={handleBlogContentChange}
                  className={styles.richTextEditor}
                />
                <div className={styles.imageInput}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewBlog({ ...newBlog, image: e.target.files[0] })}
                    id="blogImage"
                  />
                  <label htmlFor="blogImage">
                    Choose a cover image
                  </label>
                </div>
                <button
                  onClick={handleBlogSubmit}
                  className={styles.submitBlogButton}
                  disabled={!newBlog.title.trim() || !newBlog.content.trim()}
                >
                  Publish Blog
                </button>
              </div>
            )}

            <div className={styles.blogList}>
              {blogs.length === 0 ? (
                <div className={styles.emptyMessage}>
                  <p>No blogs published yet. Be the first to share your thoughts!</p>
                </div>
              ) : (
                blogs
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(renderBlogCard)
              )}
            </div>
          </div>
        )}
      </main>

      <EditThreadModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setCurrentEditThread(null);
        }}
        thread={currentEditThread}
        onSave={handleEditThread}
      />

      {expandedThread && (
        <ExpandedThreadModal
          thread={expandedThread}
          onClose={() => setExpandedThread(null)}
        />
      )}

      <CreateThreadModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleThreadSubmit}
      />

      <DialogBox
        isOpen={dialogConfig.isOpen}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        onConfirm={dialogConfig.onConfirm}
        onCancel={() => setDialogConfig({ ...dialogConfig, isOpen: false })}
      />
    </div>
  );
};

const ThreadsAndBlogsWithContext = () => {
  return (
    <ThreadContextProvider>
      <ThreadsAndBlogs />
    </ThreadContextProvider>
  );
};

export default ThreadsAndBlogsWithContext;